import React from "react";
import { Link } from "react-router-dom";
import { Avatar, AvatarGroup, Box, Button, Paper, Stack, Typography, Tooltip } from "@mui/material";
import { Calendar, CalendarHeart, Clock } from "@phosphor-icons/react";
import { format, formatRelative, isPast, isBefore, subHours, isSameWeek } from "date-fns";

import { BILLING_TYPES } from "constants";
import NextAppointmentImage from "assets/next_appointment.png";
import getAvatarInitials from "utils/getAvatarInitials";
import { ModuleContainer } from "../styles";
import ModuleHeader from "../ModuleHeader";
import EmptyNextAppointment from "./EmptyNextAppointment";

/** @typedef {import("api/typesDef").NextCall} NextCall */
/** @typedef {import("api/typesDef").ClinicianBasicInfo} ClinicianBasicInfo */

const DOXY_URL = process.env.DOXY_URL;

/**
 * NextAppointment component
 * This component is used to display the next appointment for a clinician.
 *
 * @param {Object} props
 * @param {NextCall} props.appointmentData - The data of the appointment.
 * @param {ClinicianBasicInfo} props.clinicianInfo - The clinician info.
 * @param {boolean} props.isSmallScreen - Whether the screen is small.
 * @returns {JSX.Element} The NextAppointment component.
 */
const NextAppointmentModule = ({ appointmentData, clinicianInfo, isSmallScreen }) => {
  if (!appointmentData || !appointmentData?.scheduled_date) {
    return <EmptyNextAppointment />;
  }

  const {
    id,
    billing_type,
    call_duration,
    last_name,
    scheduled_date,
    parent_name,
    video_key,
    client_name,
    off_platform,
  } = appointmentData;
  const start = new Date(scheduled_date);
  const end = new Date(start.getTime() + call_duration * 60000);
  // if start date is in the current week, show relative date if not, show the date like "Monday, July 7th"
  const startDate = isSameWeek(start, new Date())
    ? formatRelative(start, new Date()).split(" at")[0]
    : format(start, "EEEE, MMMM do");

  const parentName = `${parent_name} ${last_name}`;
  const serviceName = BILLING_TYPES[billing_type];
  const isBeforeStart = isBefore(new Date(), start);

  // check if the appointment is in the past or if the appointment is more than 2 hours in the future
  const isJoinButtonDisabled =
    !video_key || isPast(end) || isBefore(new Date(), subHours(start, 2));

  const joinButtonLink = off_platform ? `${DOXY_URL}` : `/video/${video_key}`;

  const eventAttendees = [
    {
      id: "attendee-1",
      name: parentName,
      avatar: null,
    },
    {
      id: "attendee-2",
      name: `${clinicianInfo.first_name} ${clinicianInfo.last_name}`,
      avatar: clinicianInfo.image_url,
    },
  ];

  return (
    <Paper component="section" elevation={2} sx={ModuleContainer}>
      <ModuleHeader Icon={CalendarHeart} title="Next appointment" />
      <Stack direction="row" columnGap={7} mt={7} alignItems="center">
        <Box>
          <AvatarGroup max={3} sx={{ justifyContent: "flex-end" }}>
            {eventAttendees.map((attendee) => (
              <Tooltip key={attendee.id} title={attendee.name} arrow placement="top">
                <Avatar
                  alt={attendee.name}
                  src={attendee.avatar}
                  sx={{ width: 40, height: 40, fontSize: "12px" }}
                  data-testid={`avatar-item-${attendee.id}`}
                >
                  {getAvatarInitials(attendee.name)}
                </Avatar>
              </Tooltip>
            ))}
          </AvatarGroup>

          <Typography component="h3" variant="h3" sx={{ mt: 2 }}>
            {serviceName} with {client_name}
          </Typography>

          <Stack
            direction={isSmallScreen ? "column" : "row"}
            columnGap={7}
            rowGap={2}
            mb={7}
            alignItems={isSmallScreen ? "flex-start" : "center"}
          >
            <Typography component="p" variant="body1" color="text.secondary">
              <span style={{ verticalAlign: "bottom", lineHeight: "1" }}>
                <Clock size={20} weight="duotone" />
              </span>{" "}
              {format(start, "h:mm")} - {format(end, "p")}
            </Typography>

            <Typography
              component="p"
              variant="body1"
              color="text.secondary"
              textTransform="capitalize"
            >
              <span style={{ verticalAlign: "middle", lineHeight: "1" }}>
                <Calendar size={20} weight="duotone" />
              </span>{" "}
              {startDate}
            </Typography>
          </Stack>

          <Button
            variant="contained"
            disabled={isJoinButtonDisabled}
            color="primary"
            size="large"
            href={joinButtonLink}
          >
            Join Appointment
          </Button>

          {isBeforeStart && (
            <Typography component="p" variant="body1" sx={{ mt: 5 }} color="primary.main">
              <Link
                to={`/clinician-schedule/create-event?id=${id}&isNonVideoEvent=false`}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                Manage Appointment
              </Link>
            </Typography>
          )}
        </Box>

        <Box sx={{ display: { sm: "none", md: "block", lg: "none", xl: "block" } }}>
          <img
            src={NextAppointmentImage}
            width={223}
            alt="Next appointment"
            data-testid="next-appointment-image"
            aria-hidden={isSmallScreen}
          />
        </Box>
      </Stack>
    </Paper>
  );
};

export default NextAppointmentModule;
